import { Map, fromJS } from 'immutable';
import { normalize } from 'normalizr';

import { Customer, customerSchema } from '@sophocles/entities/Customer/customerRecord';
import { receiveCustomerOptions } from '@sophocles/entities/CustomerOption';
import { receiveCustomerPreferences } from '@sophocles/entities/CustomerPreference';
import { mapListToMaps, normalizeList } from '@sophocles/entities/entityUtils';
import { addToast } from '@sophocles/features/toast';
import apiClient from '@sophocles/http-common';
import {
  checkHttpStatus,
  dataTableParamFormatter,
  generateQueryParams,
  genericErrorAction,
  mapToQueryParams,
} from '@sophocles/utils';

/* RECEIVE ACTIONS */
export const RECEIVE_CUSTOMERS = 'RECEIVE_CUSTOMERS';
export function receiveCustomers(json) {
  return {
    type: RECEIVE_CUSTOMERS,
    customers: mapListToMaps(json),
  };
}

export const RECEIVE_CUSTOMER = 'RECEIVE_CUSTOMER';
function receiveCustomer(json) {
  return {
    type: RECEIVE_CUSTOMER,
    customers: Customer(json),
  };
}

/* REQUEST ACTIONS */
export const REQUEST_CUSTOMER = 'REQUEST_CUSTOMER';
function requestCustomer(customer) {
  return {
    type: REQUEST_CUSTOMER,
    customer,
  };
}

export const REQUEST_ALL_CUSTOMERS = 'REQUEST_ALL_CUSTOMERS';
function requestAllCustomers() {
  return {
    type: REQUEST_ALL_CUSTOMERS,
  };
}

/* FETCH ACTIONS */
export function fetchCustomer(customer_id) {
  return dispatch => {
    dispatch(requestCustomer(customer_id));
    return apiClient
      .get(`/customers/${customer_id}/`)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, customerSchema))
      .then(json => {
        dispatch(receiveCustomerPreferences(json.entities.customerPreferences));
        dispatch(receiveCustomerOptions(json.entities.customerOptions));
        return dispatch(receiveCustomers(json.entities.customers));
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_CUSTOMER)));
  };
}

export function fetchRawCustomer(customer_id) {
  return dispatch => apiClient.get(`/customers/${customer_id}/`).then(response => fromJS(response.data));
}

const addSortAndSearch = (sort, sortDesc, search) =>
  (sort ? `&o=${sortDesc ? '-' : ''}${sort}` : '') + (search ? `&text_search=${search}` : '');

const addCustomFilters = (needsBilling, invoiceFrequency, customerType) =>
  (needsBilling ? `&needs_billing=${needsBilling}` : '') +
  (invoiceFrequency ? `&invoice_frequency=${invoiceFrequency}` : '') +
  (customerType ? `&is_billboard=${customerType}` : '');

export const REQUEST_CUSTOMER_BILLING_LIST = 'REQUEST_CUSTOMER_BILLING_LIST';
export function fetchCustomerBillingList(year, month, needsBilling, invoiceFrequency, customerType) {
  return ({ limit, offset, search, sort, sortDesc }) =>
    dispatch => {
      dispatch({
        type: REQUEST_CUSTOMER_BILLING_LIST,
        year,
        month,
        needsBilling,
      });

      const url =
        `/customers/billinglists/?limit=${limit}&offset=${offset}&year=${year}&month=${month}` +
        addSortAndSearch(sort, sortDesc, search) +
        addCustomFilters(needsBilling, invoiceFrequency, customerType);

      return apiClient
        .get(url)
        .then(response => fromJS(response.data))
        .catch(error => dispatch(genericErrorAction(error, REQUEST_CUSTOMER_BILLING_LIST)));
    };
}

// get all non-prospects
export function fetchAllPayingCustomers() {
  return dispatch => {
    dispatch(requestAllCustomers());
    return apiClient
      .get('/customers/', { params: { limit: 10000, is_prospect: false } })
      .then(response => {
        return normalizeList(response.data.results, 'customers', customerSchema);
      })
      .then(json => {
        dispatch(receiveCustomerOptions(json.entities.customerOptions));
        return dispatch(receiveCustomers(json.entities.customers));
      });
  };
}

export function fetchCustomersMinimal({ limit, offset, search, order = 'asc', orderBy = 'id' }) {
  return apiClient
    .get('/customers/minimal/', { params: generateQueryParams({ limit, offset, search, order, orderBy }) })
    .then(response => response.data);
}

export function fetchListOfAllPayingCustomers() {
  return dispatch => {
    dispatch(requestAllCustomers());
    return apiClient
      .get('/customers/minimal/', { params: { is_prospect: false, limit: 10000 } })
      .then(response => {
        return fromJS(response.data.results);
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

export function fetchCustomerListWithFilters(offset, limit, filterObject, search, searchFields = []) {
  return dispatch => {
    const params = { offset, limit, is_prospect: false, ...filterObject };
    if (search) {
      params.text_search = searchFields.join(',') + '|' + search;
    }
    return apiClient
      .get('/customers/minimal/', { params })
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, 'REQUEST_CUSTOMERS_FOR_LIST')));
  };
}

// get prospects
export function fetchAllProspects() {
  return dispatch => {
    dispatch(requestAllCustomers());
    return apiClient
      .get('/customers/', { params: { is_prospect: true } })
      .then(response => normalizeList(response.data.results, 'customers', customerSchema))
      .then(json => dispatch(receiveCustomers(json.entities.customers)));
  };
}

// get all, no discrimination
export function fetchAllCustomers() {
  return dispatch => {
    return apiClient
      .get(`/customers/`)
      .then(response => normalizeList(response.data.results, 'customers', customerSchema))
      .then(json => dispatch(receiveCustomers(json.entities.customers)));
  };
}

export function fetchResellers() {
  return dispatch => {
    return apiClient
      .get('/customers/', { params: { options__is_reseller: true } })
      .then(response => fromJS(response.data.results))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

// Fetch customer resellers
export function fetchCustomerResellers(customerId) {
  return dispatch => {
    return apiClient
      .get(`/customers/${customerId}/resellers/`)
      .then(response => fromJS(response.data.results))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

// Put customer resellers
export function putCustomerResellers(values, customerId) {
  return dispatch => {
    return apiClient
      .put(`/customers/${customerId}/resellers/`, values)
      .then(response => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Reseller Status Updated!',
            level: 'success',
            dismissible: true,
          }),
        );
        return fromJS(response.data.results);
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

// Fetch reseller clients
export function fetchResellerClients(customerId) {
  return dispatch => {
    return apiClient
      .get(`/customers/${customerId}/clients/`)
      .then(response => fromJS(response.data.results))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

// Put reseller clients
export function putResellerClients(values, customerId) {
  return dispatch => {
    return apiClient
      .put(`/customers/${customerId}/clients/`, values)
      .then(response => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Reseller Clients Updated!',
            level: 'success',
            dismissible: true,
          }),
        );
        return fromJS(response.data.results);
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_CUSTOMERS)));
  };
}

export function fetchUserLoginsForCustomerInRange(customer_id, start, end) {
  return dispatch =>
    apiClient
      .get(`/customers/${customer_id}/userlogins/`, { params: { timestamp__gte: start, timestamp__lte: end } })
      .then(response => fromJS(response.data.results));
}

/* Edit Actions */
export const EDIT_CUSTOMER = 'EDIT_CUSTOMER';
export function editCustomer(customer) {
  return {
    type: EDIT_CUSTOMER,
    customer,
  };
}

export function patchCustomer(customer_id, values) {
  return dispatch => {
    dispatch(editCustomer(customer_id));
    return apiClient
      .patch(`/customers/${customer_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, customerSchema))
      .then(json => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Settings Updated!',
            level: 'success',
            dismissible: true,
          }),
        );
        return dispatch(receiveCustomers(json.entities.customers));
      })
      .catch(error => dispatch(genericErrorAction(error, EDIT_CUSTOMER)));
  };
}

/* Create Actions */
export const CREATE_CUSTOMER = 'CREATE_CUSTOMER';
function sayCreateCustomer(values) {
  return {
    type: CREATE_CUSTOMER,
    values: values,
  };
}

export function createCustomer(values) {
  return dispatch => {
    dispatch(sayCreateCustomer(values));
    return apiClient
      .post(`/customers/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, customerSchema))
      .then(json => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Customer Created!',
            level: 'success',
            dismissible: true,
          }),
        );
        return dispatch(receiveCustomers(json.entities.customers));
      })
      .catch(error => dispatch(genericErrorAction(error, CREATE_CUSTOMER)));
  };
}

export function fetchCurfewReport(customerId, params) {
  return dispatch => {
    return apiClient
      .get(`/customers/${customerId}/curfewreport/`, { params })
      .then(checkHttpStatus)
      .then(response => response.data)
      .catch(error => dispatch(genericErrorAction(error, 'FETCH_CUSTOMER_CURFEW_REPORT')));
  };
}

export function fetchCurfewReportEnabled(customerId) {
  return dispatch => {
    return apiClient
      .get(`/customers/${customerId}/enabledcurfewreport/`)
      .then(checkHttpStatus)
      .then(response => response.data.enabled)
      .catch(error => dispatch(genericErrorAction(error, 'FETCH_CUSTOMER_ENABLED_CURFEW_REPORT')));
  };
}

export function fetchSiteListingForCustomer(customerId) {
  return dispatch => {
    dispatch(requestCustomer(customerId));
    return apiClient
      .get(`/sites/xlsx/`, { params: { customer_id: customerId }, responseType: 'blob' })
      .then(checkHttpStatus)
      .then(response => response.data)
      .catch(error => dispatch(genericErrorAction(error, REQUEST_CUSTOMER)));
  };
}
